// src/lib/hooks/useCometChat.ts

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CometChat,
} from "@cometchat/chat-sdk-javascript";

export function useCometChat() {
  const [user, setUser] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [activeConversation, setActiveConversation] = useState<any>(null);

  // Typing: map uid → boolean
  const [typingUsers, setTypingUsers] = useState<Record<string, boolean>>({});

  // Presence: map uid → { online: boolean, lastSeen?: number }
  const [presence, setPresence] = useState<Record<string, any>>({});

  // Pagination request instance (same req loads older pages)
  const [messagesRequest, setMessagesRequest] = useState<any>(null);

  // Listener IDs
  const messageListenerId = useRef<string>("");
  const userListenerId = useRef<string>("");

  // Load current logged-in user
  useEffect(() => {
    (async () => {
      try {
        const u = await CometChat.getLoggedinUser();
        console.log(u)
        if (u) {
          setUser(u);
          console.log(u)
          loadUsers(u);
          loadConversations(u);    
        }
      } catch (e) {
        console.warn("Failed getLoggedinUser:", e);
      }
    })();
  }, []);

  const loadUsers = useCallback(async (user:any) => {
    try {
      const req = new CometChat.UsersRequestBuilder().setLimit(50).build();
      const res = await req.fetchNext();
      setUsers(res || []);0
    } catch (err) {
      console.error("loadUsers:", err);
    }
  }, []);

  const loadConversations = useCallback(async (user:any) => {
    if (!user) return;  // 🚨 Prevent API call before login

    try {
      const req = new CometChat.ConversationsRequestBuilder()
        .setLimit(50)
        .build();

      const res = await req.fetchNext();
      setConversations(res || []);
    } catch (err) {
      console.error("loadConversations:", err);
    }
  }, [user]);


  // Open a conversation and load initial messages
  const openConversation = useCallback(async (conv: any | null) => {
    setActiveConversation(conv);
    setMessages([]);
    setMessagesRequest(null);

    if (!conv) return;

    const isGroup = conv.conversationType === "group";

    let req;

    if (isGroup) {
    req = new CometChat.MessagesRequestBuilder()
        .setLimit(50)
        .setGUID(conv.conversationWith.guid)
        .build();
    } else {
    req = new CometChat.MessagesRequestBuilder()
        .setLimit(50)
        .setUID(conv.conversationWith.uid)
        .build();
    }
    setMessagesRequest(req);

    try {
        // IMPORTANT: first load MUST be fetchNext()
        const initial = await req.fetchPrevious();
        setMessages(initial || []);

        // Mark last message read
        if (initial && initial.length > 0) {
            const last = initial[initial.length - 1];

            const receiverId = isGroup
            ? conv.conversationWith.guid
            : last.sender?.uid;

            const receiverType = isGroup ? "group" : "user";

            await CometChat.markAsRead(
            last.id || last.getId?.(),
            receiverId,
            receiverType,
            last.sender?.uid
            );
        }
        } catch (err) {
        console.error("openConversation error:", err);
        }
    },
    []
  );

  // Load older messages (infinite scroll)
  const loadMoreMessages = useCallback(async () => {
    if (!messagesRequest) return;

    try {
      const older = await messagesRequest.fetchPrevious();
      if (older && older.length > 0) {
        // prepend older msgs
        setMessages((prev) => [...older, ...prev]);
      }
    } catch (err) {
      console.error("loadMoreMessages:", err);
    }
  }, [messagesRequest]);

  // Send message
  const sendMessage = useCallback(
    async (text: string) => {
      if (!activeConversation) return;

      const isGroup = activeConversation?.conversationType === "group";

      const msg = new CometChat.TextMessage(
        isGroup
          ? activeConversation.conversationWith?.guid
          : activeConversation.conversationWith?.uid,
        text,
        isGroup ? CometChat.RECEIVER_TYPE.GROUP : CometChat.RECEIVER_TYPE.USER
      );

      const sent = await CometChat.sendMessage(msg);
      setMessages((prev) => [...prev, sent]);
      loadConversations();

      return sent;
    },
    [activeConversation, loadConversations]
  );

  // Local message patcher (read/delivered)
  const patchMessage = useCallback((id: any, patch: any) => {
    setMessages((prev) =>
      prev.map((m) => {
        const mid = m.id || m.getId?.();
        return String(mid) === String(id) ? { ...m, ...patch } : m;
      })
    );
  }, []);

  // Add listeners
  useEffect(() => {
    if (!user) return;

    const ml = "ml_" + user.uid;
    const ul = "ul_" + user.uid;

    messageListenerId.current = ml;
    userListenerId.current = ul;

    const mListener = new CometChat.MessageListener({
      onTextMessageReceived: (msg: any) => {
        setMessages((prev) => [...prev, msg]);
        loadConversations();
      },

      onTypingStarted: (ti: any) => {
        const uid = ti?.sender?.uid;
        if (uid) setTypingUsers((p) => ({ ...p, [uid]: true }));
      },
      onTypingEnded: (ti: any) => {
        const uid = ti?.sender?.uid;
        if (uid)
          setTypingUsers((p) => {
            const c = { ...p };
            delete c[uid];
            return c;
          });
      },

      onMessagesDelivered: (info: any) => {
        const id = info?.id || info?.messageId;
        if (id) patchMessage(id, { deliveredAt: info.deliveredAt || Date.now() });
      },
      onMessagesRead: (info: any) => {
        const id = info?.id || info?.messageId;
        if (id) patchMessage(id, { readAt: info.readAt || Date.now() });
      },
    });

    const uListener = new CometChat.UserListener({
      onUserOnline: (u) => {
        setPresence((p) => ({ ...p, [u.uid]: { online: true } }));
      },
      onUserOffline: (u) => {
        setPresence((p) => ({
          ...p,
          [u.uid]: { online: false, lastSeen: Date.now() },
        }));
      },
    });

    CometChat.addMessageListener(ml, mListener);
    CometChat.addUserListener(ul, uListener);

    return () => {
      CometChat.removeMessageListener(ml);
      CometChat.removeUserListener(ul);
    };
  }, [user, loadConversations, patchMessage]);

  return {
    user,
    users,
    conversations,
    messages,
    presence,
    typingUsers,
    activeConversation,

    openConversation,
    sendMessage,
    loadMoreMessages,
  };
}
